'use client';

/** @app — application layer; owned per-fork (LMS / OTT). See PLATFORM_FILES.md. */
import { useTranslation } from 'react-i18next';
import type { LearningCourseDetail } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';

// CareerBlock — what the course unlocks career-wise.
//
// Used to be 4 unrelated stacked cards (salary, roles, companies, growth).
// Now: one coherent grid where each card answers one specific question
// (what will I earn / what jobs / where do grads go / how fast is the
// market growing). Any missing field skips its card cleanly.

interface CareerBlockProps {
  career: NonNullable<LearningCourseDetail['careerOutcomes']>;
}

export function CareerBlock({ career }: CareerBlockProps) {
  const { t } = useTranslation('common');
  const { salaryRange, growthRate, typicalRoles, placedCompanies } = career;
  // If everything is missing, render nothing — caller is responsible
  // for not showing the section title in that case (the variant
  // orchestrator handles this).
  if (!salaryRange && !growthRate && !typicalRoles?.length && !placedCompanies?.length) {
    return null;
  }
  return (
    <div className="cd-career">
      {salaryRange && (
        <Card asChild>
          <div className="cd-career-card">
            <div className="cd-career-label">{t('label.salaryRange')}</div>
            <div className="cd-career-value">{salaryRange}</div>
            {growthRate && (
              <div className="cd-career-sub">
                {t('label.industryGrowth')} {growthRate}
              </div>
            )}
          </div>
        </Card>
      )}
      {typicalRoles && typicalRoles.length > 0 && (
        <Card asChild>
          <div className="cd-career-card">
            <div className="cd-career-label">{t('label.sampleRoles')}</div>
            <ul className="cd-career-list">
              {typicalRoles.slice(0, 5).map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        </Card>
      )}
      {placedCompanies && placedCompanies.length > 0 && (
        <Card asChild>
          <div className="cd-career-card">
            <div className="cd-career-label">{t('label.graduateEmployers')}</div>
            <ul className="cd-career-list">
              {placedCompanies.slice(0, 5).map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        </Card>
      )}
    </div>
  );
}
